import { Button } from "@mui/material";
import { Divider } from "@mui/material";
import {
  DataGrid,
  GridCellEditStopParams,
  GridRenderCellParams,
  GridRowId,
} from "@mui/x-data-grid";
import SearchBar from "material-ui-search-bar";
import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import { useNavigate, useParams } from "react-router-dom";
import { Axios } from "../App";
import styles from "../styles/Datatable.module.scss";
import * as FaIcons from "react-icons/fa";

type TDataType = {
  id: string;
  participant_name: string;
  number_of_additional_guests: number;
  note: string;
  is_participated: string;
  field1: string;
  field2: string;
  field3: string;
};

const DataTable: React.FC = () => {
  const navigate = useNavigate();
  const alert = useAlert();
  const { id } = useParams();
  const [originalRows, setOriginalRows] = useState<TDataType[]>([]);
  const [rows, setRows] = useState<TDataType[]>(originalRows);
  const [searched, setSearched] = useState("");
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [columns, setColumns] = useState([]);

  const getQueries = async () => {
    isEventExists();
    getAllByID();
  };

  useEffect(() => {
    getQueries();
  }, []);

  const requestSearch = (searchedVal: string) => {
    const filteredRows = originalRows.filter((row) => {
      return (
        row.participant_name
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .includes(
            searchedVal
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
          ) ||
        row.note
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .includes(
            searchedVal
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
          ) ||
        row.field1
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .includes(
            searchedVal
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
          ) ||
        row.field2
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .includes(
            searchedVal
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
          ) ||
        row.field3
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .includes(
            searchedVal
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
          )
      );
    });
    setRows(filteredRows);
  };

  const cancelSearch = () => {
    setSearched("");
    requestSearch(searched);
  };

  const setHeader = (firstRowOfDatabase: TDataType) => {
    let array = [] as any;
    if (!firstRowOfDatabase) {
      setColumns(array);
      return;
    }
    array.push(
      {
        field: "is_participated",
        headerName: "Résztvevő?",
        renderCell: (params: GridRenderCellParams) => {
          return params.value === 1 ? (
            <FaIcons.FaCheck color="darkGreen" />
          ) : (
            <FaIcons.FaTimes color="red" />
          );
        },
      },
      {
        field: "participant_name",
        headerName: firstRowOfDatabase?.participant_name || "Név",
        editable: true,
        minWidth: 400,
      },
      {
        field: "number_of_additional_guests",
        headerName: "HVSZ",
        editable: true,
        width: 50,
      },
      {
        field: "note",
        headerName: firstRowOfDatabase?.note || "Megjegyzés",
        editable: true,
        minWidth: 400,
      }
    );
    if (firstRowOfDatabase?.field1) {
      array.push({
        field: "field1",
        headerName: firstRowOfDatabase?.field1 || "Mező",
        editable: true,
        minWidth: 400,
      });
    }
    if (firstRowOfDatabase?.field2) {
      array.push({
        field: "field2",
        headerName: firstRowOfDatabase?.field2 || "Mező 2",
        editable: true,
        minWidth: 400,
      });
    }
    if (firstRowOfDatabase?.field3) {
      array.push({
        field: "field3",
        headerName: firstRowOfDatabase?.field3 || "Mező 3",
        editable: true,
        minWidth: 400,
      });
    }
    setColumns(array);
  };

  const getAllByID = () => {
    Axios.post("participants/getAllByID.php", {
      id: id,
    })
      .then(({ data }) => {
        setHeader(data[0]);
        data.shift();
        setOriginalRows(data);
        setRows(data);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const isEventExists = () => {
    Axios.post("events/isEventExists.php", {
      id: id,
    })
      .then(({ data }) => {
        if (!data?.isExists) navigate("/event-manager");
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const removeParticipantsAndGetAll = () => {
    selectedRows.map((e) => {
      Axios.post("participants/deleteParticipantsByID.php", {
        id: e,
      })
        .then(({ data }) => {
          if (!data?.success) throw new Error(data?.message);
        })
        .then(() => {
          getAllByID();
        })
        .catch((e) => {
          console.error(e);
          alert.error("Valami nem stimmelt a törlés során.");
        });
    });
  };

  const removeAllParticipantsAndGetAll = () => {
    Axios.post("participants/deleteAllParticipantsDataByEventID.php", {
      id: id,
    })
      .then(({ data }) => {
        if (!data?.success) throw new Error(data?.message);
      })
      .then(() => {
        getAllByID();
      })
      .catch((e) => {
        console.error(e);
        alert.error("Valami nem stimmelt a törlés során.");
      });
  };

  const updateParticipant = ({
    id,
    participant_name,
    number_of_additional_guests,
    note,
    field1,
    field2,
    field3,
  }: {
    id: string;
    participant_name: string;
    number_of_additional_guests: number;
    note: string;
    field1: string;
    field2: string;
    field3: string;
  }) => {
    if (isNaN(number_of_additional_guests)) {
      alert.error("A vendégek száma csak szám lehet.");
      return;
    }
    Axios.post("participants/updateParticipant.php", {
      id: id,
      participant_name: participant_name,
      number_of_additional_guests: number_of_additional_guests,
      note: note,
      field1: field1,
      field2: field2,
      field3: field3,
    })
      .then(({ data }) => {
        if (!data.success) throw new Error("Valami nem stimmel");
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const handleCellEditCommit = (params: GridCellEditStopParams) => {
    const array = rows.map((r) => {
      if (r.id === params.id) return { ...r, [params.field]: params.value };
      else return { ...r };
    });

    setRows(array);
    setOriginalRows(array);

    let result = rows.filter((obj) => {
      return obj.id === params.id;
    });
    const dataForInsert = { ...result[0], [params.field]: params.value };

    updateParticipant(dataForInsert);
  };

  return (
    <div className={styles.wrapper}>
      <SearchBar
        className={styles.searchBar}
        value={searched}
        onChange={(searchVal) => requestSearch(searchVal)}
        onCancelSearch={() => cancelSearch()}
        placeholder="Keresés..."
      />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="contained"
          color="error"
          onClick={removeParticipantsAndGetAll}
        >
          Kijelölt(ek) törlése
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={removeAllParticipantsAndGetAll}
        >
          Az összes törlése
        </Button>
      </div>
      <Divider sx={{ m: 2 }} />
      <>Kijelötek száma: {selectedRows.length}</>
      <Divider sx={{ m: 2 }} />
      <DataGrid
        // @ts-ignore
        getRowClassName={(params) => {
          if (Number(params.row.is_participated) === 1) return "isParticipated";
        }}
        rows={rows}
        columns={columns}
        pageSize={50}
        rowsPerPageOptions={[50]}
        checkboxSelection
        sx={{
          boxShadow: 2,
          border: 2,
          overflowX: "scroll",
        }}
        onRowSelectionModelChange={(ids) => {
          setSelectedRows(ids);
        }}
        rowSelectionModel={selectedRows}
        disableSelectionOnClick
        onCellEditCommit={(params: GridCellEditStopParams) =>
          handleCellEditCommit(params)
        }
      />
    </div>
  );
};

export default React.memo(DataTable);
